import { isInteger, indexOf, uniqBy } from 'lodash';
import dayjs from 'dayjs';
import isVod from '../helpers/isVod';

const DATE_FORMAT = 'YYYY-MM-DD';

const RATING_GROUPS = {
  0: ['0'],
  6: ['0', '6'],
  12: ['0', '6', '12', '12 mit Eltern ab 6 J.'],
};

const RELEASE_TYPE_GROUPS = {
  '2D': [
    101,
    111,
    112,
    201,
    211,
    213,
    501,
    511,
    513,
    301,
    311,
    313,
    0,
    '--none--',
  ],
  '3D': [202, 212, 214, 302, 312, 314, 502, 503, 512, 514, 602, 612],
  '4DX': [601, 602, 611, 612],
  'Original Version': [
    111,
    112,
    211,
    213,
    311,
    313,
    513,
    212,
    214,
    312,
    314,
    503,
    514,
    611,
    612,
  ],
};

const filmHasAttribute = ({ attributes }, attrName) => {
  const found = attributes.find(
    (attribute) => `f-${attribute.name}` === attrName
  );
  return found ? true : false;
};

const performanceHasAttribute = ({ attributes }, attrName) => {
  const found = attributes.find(
    (attribute) => `p-${attribute.name}` === attrName
  );
  return found ? true : false;
};

const auditoriumHasAttribute = ({ attributes }, attrName) => {
  const found = attributes.find(
    (attribute) => `a-${attribute.name}` === attrName
  );
  return found ? true : false;
};

const getPerformanceDayTime = ({ showTime }) => {
  let type = 'spaet';
  if (showTime <= '17:59') {
    type = 'tagsueber';
  } else if (showTime <= '21:59') {
    type = 'abends';
  }

  return type;
};

const filterSites = ({ performances }, siteIds) => {
  if (!performances.length) {
    return [];
  }
  const matchingPerformances = performances.filter(
    (perf) => indexOf(siteIds, `${perf.siteId}`) > -1
  );

  return matchingPerformances;
};

const filterRatingGroup = ({ rated, performances }, rating) => {
  if (!rated) {
    return [];
  }

  const group = RATING_GROUPS[rating] || [];
  if (indexOf(group, rated) > -1) {
    return performances;
  }

  return [];
};

const filterReleaseTypeGroups = ({ performances }, groups) => {
  if (!performances.length) {
    return [];
  }
  let releaseTypeIdsToFilter = [];
  groups.forEach((group) => {
    const filterGroup = RELEASE_TYPE_GROUPS[group] || [];
    releaseTypeIdsToFilter = [...releaseTypeIdsToFilter, ...filterGroup];
  });
  const matchingPerformances = performances.filter(
    ({ releaseTypeId }) => indexOf(releaseTypeIdsToFilter, releaseTypeId) > -1
  );

  return matchingPerformances;
};

const filterPerformancesAfterDate = ({ performances }, date) => {
  if (!performances.length) {
    return [];
  }
  const dateFrom = dayjs(date, DATE_FORMAT);
  if (!dateFrom.isValid()) {
    return [];
  }
  const matchingPerformances = performances.filter(({ actualDate }) => {
    const perfDate = dayjs(actualDate, DATE_FORMAT);
    return perfDate.isSame(dateFrom) || perfDate.isAfter(dateFrom);
  });

  return matchingPerformances;
};

const filterPerformancesBeforeDate = ({ performances }, date) => {
  if (!performances.length) {
    return [];
  }
  const dateTo = dayjs(date, DATE_FORMAT);
  if (!dateTo.isValid()) {
    return [];
  }
  const matchingPerformances = performances.filter(({ actualDate }) => {
    const perfDate = dayjs(actualDate, DATE_FORMAT);
    return perfDate.isSame(dateTo) || perfDate.isBefore(dateTo);
  });

  return matchingPerformances;
};

const filterPerformancesAtDayTimes = ({ performances }, dayTimes) => {
  if (!performances.length) {
    return [];
  }
  const matchingPerformances = performances.filter(
    (perf) => indexOf(dayTimes, getPerformanceDayTime(perf)) > -1
  );

  return matchingPerformances;
};

const filterAttributes = (film, attrsToFilter) => {
  const filmAttributes = attrsToFilter.filter((filter) =>
    filter.startsWith('f-')
  );
  const perfAttributes = attrsToFilter.filter((filter) =>
    filter.startsWith('p-')
  );
  const auditoriumAttributes = attrsToFilter.filter((filter) =>
    filter.startsWith('a-')
  );

  let matchingFilmAttributes = [];
  if (filmAttributes.length) {
    matchingFilmAttributes = filmAttributes.filter((value) =>
      filmHasAttribute(film, value)
    );
    // check if early exit is possible
    if (!matchingFilmAttributes.length) {
      // no matching filmAttributes
      return [];
    } else if (!perfAttributes.length && !auditoriumAttributes.length) {
      // maching filmAttributes and no other attributes to filter
      return film.performances;
    }
  }

  let matchingPerformances = [];
  if (perfAttributes.length) {
    perfAttributes.forEach((perfAttr) => {
      const perfsWithCurrentAttr = film.performances.filter((perf) =>
        performanceHasAttribute(perf, perfAttr)
      );
      matchingPerformances = [...matchingPerformances, ...perfsWithCurrentAttr];
    });
  }
  if (auditoriumAttributes.length) {
    auditoriumAttributes.forEach((auditoriumAttr) => {
      const perfsWithCurrentAttr = film.performances.filter(({ auditorium }) =>
        auditoriumHasAttribute(auditorium, auditoriumAttr)
      );
      matchingPerformances = [...matchingPerformances, ...perfsWithCurrentAttr];
    });
  }

  // remove doubled performances (matching auditorium and performance attribute)
  matchingPerformances = uniqBy(matchingPerformances, 'id');

  // only return matching performances if there are attributes to filter
  if (perfAttributes.length || auditoriumAttributes.length) {
    return matchingPerformances;
  }

  // otherwise return the given performances just to be safe
  // (should not be reached if there were actual attributes to filter)
  return film.performances;
};

const filterPerformances = (film, givenFilter) => {
  const { filter, isTopFilter, isDealFilter, isVodFilter } = givenFilter;
  
  if (isTopFilter) {
    if (!isInteger(film.topTenPosition)) {
      film.performances = [];
    }
    return film;
  }

  if (isDealFilter) {
    if (!film.deal) {
      film.performances = [];
    }
    return film;
  }

  if (isVodFilter) {
    if (!isVod(film)) {
      film.performances = [];
    }
    return film;
  }
  
  if (!isVodFilter && isVod(film)) {
    film.performances = [];

    return film;
  }

  if (!filter) {
    //console.log('no actual filter!');
    return film;
  }

  Object.keys(filter).forEach((appliedFilter) => {
    const filterValue = filter[appliedFilter];
    switch (appliedFilter) {
      case 'site':
        film.performances = filterSites(film, filterValue);
        return;
      case 'fsk':
        film.performances = filterRatingGroup(film, filterValue);
        return;
      case 'releaseType':
        film.performances = filterReleaseTypeGroups(film, filterValue);
        return;
      case 'dateFrom':
        film.performances = filterPerformancesAfterDate(film, filterValue);
        return;
      case 'dateTo':
        film.performances = filterPerformancesBeforeDate(film, filterValue);
        return;
      case 'performanceTimeType':
        film.performances = filterPerformancesAtDayTimes(film, filterValue);
        return;
      case 'attributes':
        film.performances = filterAttributes(film, filterValue);
        return;
      case 'scope-type':
        return;
      default:
        console.log('Unknown filter!', appliedFilter);
        return;
    }
  });

  return film;
};

export default (films, filter) => {
  return new Promise((resolve, reject) => {
    try {
      const filmsCopied = films.map((film) => ({
        ...film,
        performances: [...film.performances],
      }));
      const filmsReduced = filmsCopied.map((film) =>
        filterPerformances(film, filter)
      );
      const filmsFiltered = filmsReduced.filter(
        ({ performances }) => performances.length > 0
      );

      // setTimeout(() => {
      //   console.log('filteredFilms', filmsFiltered);
      //   resolve(filmsFiltered);
      // }, 500);
      resolve(filmsFiltered);
    } catch (error) {
      console.log(error);
      reject({ message: 'filtering failed' });
    }
  });
};
